import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, FIRESTORE_ROOT } from '../firebase';
import { compressImageFile } from '../utils/imageCompression';

export type UploadFolder = 'experiences' | 'genius-loci' | 'interactions' | 'media' | 'organizations';

export async function uploadFile(
  file: File,
  folder: UploadFolder,
  entityId: string | number,
): Promise<string> {
  const uploadFileData = await compressImageFile(file, {
    maxWidth: 1600,
    maxHeight: 1600,
    maxBytes: 750 * 1024,
  });
  const ext = uploadFileData.name.split('.').pop() ?? 'bin';
  const timestamp = Date.now();
  const path = `${FIRESTORE_ROOT}/${folder}/${entityId}/${timestamp}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, uploadFileData);
  return getDownloadURL(storageRef);
}

export async function deleteFile(url: string): Promise<void> {
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
  } catch {
    // ignore if already deleted
  }
}

function normalizeFileExtension(name: string): string {
  const raw = String(name || '').split('.').pop() || 'png';
  return raw.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().slice(0, 5) || 'png';
}

/** DB / legacy limit (see legacyApiService EXPERIENCE_LIMITS.image) */
const EXPERIENCE_IMAGE_DB_MAX_LEN = 50;

function truncatePreservingExtension(filename: string, maxLen: number): string {
  const trimmed = String(filename || '').trim();
  if (!trimmed || trimmed.length <= maxLen) return trimmed;
  const lastDot = trimmed.lastIndexOf('.');
  if (lastDot <= 0 || lastDot >= trimmed.length - 1) {
    return trimmed.slice(0, maxLen);
  }
  const ext = trimmed.slice(lastDot + 1);
  const base = trimmed.slice(0, lastDot);
  const maxBase = maxLen - ext.length - 1;
  if (maxBase < 1) return trimmed.slice(0, maxLen);
  return `${base.slice(0, maxBase)}.${ext}`;
}

/**
 * Long numeric ids (es. Date.now() durante "nuova esperienza") producono nomi lunghi e
 * rompevano il vecchio .slice(0,24) a metà estensione → oggetto su Storage ≠ valore in DB → 404 gcs-object.
 */
function shortenIdForCoverFilename(experienceId: string | number): string {
  const raw = String(experienceId ?? '').trim();
  if (!raw) return '0';
  if (/^\d+$/.test(raw) && raw.length > 10) {
    try {
      return `n${BigInt(raw).toString(36)}`;
    } catch {
      return raw.slice(-12);
    }
  }
  return raw.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 14) || '0';
}

/**
 * Upload experience cover in legacy-compatible storage path and return:
 * - `storedValue`: compact DB-safe key (filename)
 * - `downloadUrl`: absolute URL for immediate preview
 */
export async function uploadExperienceCover(
  file: File,
  experienceId: string | number,
): Promise<{ storedValue: string; downloadUrl: string }> {
  const uploadFileData = await compressImageFile(file, {
    maxWidth: 1600,
    maxHeight: 1600,
    maxBytes: 600 * 1024,
  });
  const ext = normalizeFileExtension(uploadFileData.name);
  const idPart = shortenIdForCoverFilename(experienceId);
  const uniq = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 4)}`;
  let compactName = `e${idPart}_${uniq}.${ext}`;
  compactName = truncatePreservingExtension(compactName, EXPERIENCE_IMAGE_DB_MAX_LEN);
  const objectPath = `${FIRESTORE_ROOT}/legacy/experiences/${compactName}`;
  const storageRef = ref(storage, objectPath);
  await uploadBytes(storageRef, uploadFileData);
  const downloadUrl = await getDownloadURL(storageRef);
  return { storedValue: compactName, downloadUrl };
}

/**
 * Upload organization logo in legacy-compatible storage path and return:
 * - `storedValue`: compact filename (same pattern as experience covers) → DB-safe varchar(50)
 * - `downloadUrl`: absolute URL for immediate preview
 *
 * Il file viene caricato sotto glooci_genia/legacy/organizations/ (percorso pubblico condiviso
 * con le risorse legacy migrate) così il proxy GCS e l'app mobile lo trovano senza token.
 */
export async function uploadOrganizationLogo(
  file: File,
  organizationId: string | number,
): Promise<{ storedValue: string; downloadUrl: string }> {
  const uploadFileData = await compressImageFile(file, {
    maxWidth: 900,
    maxHeight: 900,
    maxBytes: 300 * 1024,
  });
  const ext = normalizeFileExtension(uploadFileData.name);
  const idRaw = String(organizationId ?? '').trim();
  const idPart = /^\d+$/.test(idRaw) && idRaw.length > 8
    ? `n${BigInt(idRaw).toString(36)}`
    : (idRaw.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 10) || '0');
  const uniq = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 4)}`;
  let compactName = `o${idPart}_${uniq}.${ext}`;
  compactName = truncatePreservingExtension(compactName, 20);
  const objectPath = `${FIRESTORE_ROOT}/legacy/organizations/${compactName}`;
  const storageRef = ref(storage, objectPath);
  await uploadBytes(storageRef, uploadFileData);
  const downloadUrl = await getDownloadURL(storageRef);
  return { storedValue: compactName, downloadUrl };
}
